import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MoreVertical, Copy, Trash2, Link as LinkIcon, Database } from 'lucide-react';
import { format } from 'date-fns';
import { getFileIconComponent } from '@/utils/fileIcons';
import { formatFileSize, formatNumber } from '@/utils/formatters';
import { getStatusConfig } from '@/utils/statusHelpers';

import type { RAGDocument } from '@/types/rag';
import { CardDetailActions } from './CardDetailActions';

interface DocumentGridCardProps {
    doc: RAGDocument;
    isSelected: boolean;
    onToggleSelect: (id: string, selected: boolean) => void;
    onView: (doc: RAGDocument) => void;
    onDelete: (id: string) => void;
    index: number;
}

export const DocumentGridCard: React.FC<DocumentGridCardProps> = ({
    doc,
    isSelected,
    onToggleSelect,
    onView,
    onDelete,
    index
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showCopiedToast, setShowCopiedToast] = useState(false);
    const [showMenu, setShowMenu] = useState(false);

    const handleCopyId = async (e: React.MouseEvent) => {
        e.stopPropagation();
        await navigator.clipboard.writeText(doc.id);
        setShowCopiedToast(true);
        setTimeout(() => setShowCopiedToast(false), 2000);
    };

    useEffect(() => {
        if (!showMenu) return;
        const handleClickOutside = () => setShowMenu(false);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showMenu]);

    const handleCardClick = () => {
        setIsExpanded(!isExpanded);
    };

    const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggleSelect(doc.id, !isSelected);
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMenu(false);
        onDelete(doc.id);
    };

    const handleViewChunks = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMenu(false);
        onView(doc);
    };

    const statusConfig = getStatusConfig(doc.status);
    const FileIcon = getFileIconComponent(doc.mime_type);

    return (
        <motion.div
            {...({
                layout: true,
                initial: { opacity: 0, y: 20 },
                animate: { opacity: 1, y: 0 },
                exit: { opacity: 0, scale: 0.8 }
            } as any)}
            transition={{ duration: 0.2, delay: index * 0.05 }}
            className={`group relative rounded-2xl p-[1px] bg-gradient-to-br from-cyan-500/20 via-purple-500/20 to-pink-500/20 hover:from-cyan-500/40 hover:via-purple-500/40 hover:to-pink-500/40 transition-all duration-300 ${isSelected ? 'ring-2 ring-indigo-500 bg-indigo-500/5' : ''
                } ${isExpanded ? 'z-10' : 'z-1'}`}
            onClick={handleCardClick}
            role="button"
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for ${doc.filename}`}
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter') handleCardClick();
                if (e.key === ' ') {
                    e.preventDefault();
                    onToggleSelect(doc.id, !isSelected);
                }
            }}
        >
            {/* Hover Glow Effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-20 bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 blur-xl transition-all duration-500" />

            {/* Inner Content */}
            <div className="relative h-full bg-[#0A0A0A] rounded-2xl p-4 sm:p-5 flex flex-col overflow-hidden">
                {/* Checkbox - Top Right */}
                <div className="absolute top-4 right-4 z-20" onClick={handleCheckboxClick}>
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => { }}
                        className="w-6 h-6 sm:w-5 sm:h-5 rounded-full border-2 border-gray-600 bg-transparent checked:bg-indigo-500 checked:border-indigo-500 cursor-pointer transition-colors"
                    />
                </div>

                {/* Context Menu - Top Right (next to checkbox) */}
                <div className="absolute top-4 right-12 z-20" onClick={(e) => e.stopPropagation()}>
                    <button
                        onClick={() => setShowMenu(!showMenu)}
                        className="p-1 text-gray-400 hover:text-white transition-colors"
                    >
                        <MoreVertical className="w-4 h-4" />
                    </button>

                    {showMenu && (
                        <div className="absolute right-0 mt-2 w-48 bg-gray-900 border border-gray-700 rounded-lg shadow-xl overflow-hidden z-30">
                            <button
                                onClick={handleViewChunks}
                                className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors flex items-center gap-2"
                            >
                                <Copy className="w-4 h-4" />
                                View Chunks
                            </button>
                            <button
                                onClick={handleCopyId}
                                className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors flex items-center gap-2"
                            >
                                <Copy className="w-4 h-4" />
                                Copy ID
                            </button>
                            <button
                                onClick={handleDelete}
                                className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors flex items-center gap-2"
                            >
                                <Trash2 className="w-4 h-4" />
                                Delete
                            </button>
                        </div>
                    )}
                </div>

                {/* Holographic Icon */}
                <div className="relative w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    {/* Aura/Glow */}
                    <div className="absolute inset-0 bg-blue-500/30 blur-2xl rounded-full animate-pulse" />

                    {/* Icon Container */}
                    <div className="relative z-10 w-full h-full bg-black/40 border border-blue-500/30 rounded-xl flex items-center justify-center backdrop-blur-sm">
                        <FileIcon className="w-8 h-8 text-blue-400 drop-shadow-[0_0_8px_rgba(96,165,250,0.5)]" />
                    </div>
                </div>

                {/* Title */}
                <h3
                    className="font-bold text-white tracking-tight text-center mb-1 line-clamp-2"
                    title={doc.filename}
                >
                    {doc.filename}
                </h3>

                {/* Metadata */}
                <p className="text-xs text-slate-400 text-center font-mono mb-6">
                    {formatFileSize(doc.file_size || 0)} • {doc.mime_type || 'unknown'}
                </p>

                {/* Metrics Badges */}
                <div className="flex flex-wrap gap-2 justify-center mb-3">
                    {/* Chunks Badge */}
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-cyan-950/30 border border-cyan-500/30 backdrop-blur-md">
                        <LinkIcon className="w-3 h-3 text-cyan-400" />
                        <span className="text-[10px] font-bold text-cyan-300 tracking-wider">
                            {doc.chunks_count || 0} CHUNKS
                        </span>
                    </div>

                    {/* Tokens Badge */}
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-950/30 border border-purple-500/30 backdrop-blur-md">
                        <Database className="w-3 h-3 text-purple-400" />
                        <span className="text-[10px] font-bold text-purple-300 tracking-wider">
                            {formatNumber(doc.total_tokens || 0)} TOKENS
                        </span>
                    </div>

                    {/* Status Badge */}
                    <div
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold tracking-wider ${statusConfig.bgClass} ${statusConfig.textClass} ${statusConfig.borderClass} border backdrop-blur-md`}
                    >
                        {statusConfig.label}
                    </div>
                </div>

                {/* Expanded Section */}
                <AnimatePresence>
                    {isExpanded && (
                        <motion.div
                            {...({
                                initial: { opacity: 0, height: 0, marginTop: 0 },
                                animate: { opacity: 1, height: 'auto', marginTop: 24 },
                                exit: { opacity: 0, height: 0, marginTop: 0 }
                            } as any)}
                            transition={{
                                type: 'spring',
                                stiffness: 300,
                                damping: 30,
                                opacity: { duration: 0.2 }
                            }}
                            className="border-t border-cyan-500/30 pt-4"
                        >
                            {/* Section Header */}
                            <h4 className="text-xs font-bold text-cyan-400 tracking-[0.2em] uppercase border-b border-cyan-500/30 pb-2 mb-4">
                                TECHNICAL DETAILS
                            </h4>

                            {/* Created */}
                            <div className="mb-3">
                                <label className="text-[10px] text-gray-500 uppercase tracking-wider block mb-1">
                                    Created
                                </label>
                                <p className="text-sm text-white font-mono leading-relaxed">
                                    {format(new Date(doc.created_at), 'MMM dd, yyyy, h:mm:ss a')}
                                </p>
                            </div>

                            {/* Processing Time */}
                            {doc.processing_time && (
                                <div className="mb-3">
                                    <label className="text-[10px] text-gray-500 uppercase tracking-wider block mb-1">
                                        Processing Time
                                    </label>
                                    <p className="text-sm text-white font-mono leading-relaxed">
                                        {doc.processing_time}s
                                    </p>
                                </div>
                            )}

                            {/* Characteristics */}
                            {doc.characteristics && (
                                <div className="mb-3">
                                    <label className="text-[10px] text-gray-500 uppercase tracking-wider block mb-1">
                                        Characteristics
                                    </label>
                                    <div className="bg-gray-900/50 border border-gray-700/50 rounded-lg p-3 backdrop-blur-sm">
                                        <p className="text-sm text-white">{doc.characteristics}</p>
                                    </div>
                                </div>
                            )}

                            {/* Document ID */}
                            <div className="mb-3">
                                <label className="text-[10px] text-gray-500 uppercase tracking-wider block mb-1">
                                    Document ID
                                </label>
                                <div className="bg-gray-900/50 border border-gray-700/50 rounded-lg p-3 backdrop-blur-sm flex items-center justify-between">
                                    <code className="text-xs text-white font-mono truncate flex-1">
                                        {doc.id}
                                    </code>
                                    <button
                                        onClick={handleCopyId}
                                        className="ml-2 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 rounded p-1 transition-colors duration-200"
                                    >
                                        <Copy className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            <CardDetailActions
                                doc={doc}
                                onView={onView}
                                onDelete={onDelete}
                            />

                            {/* Collapse Button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsExpanded(false);
                                }}
                                className="w-full py-2 text-xs text-gray-400 hover:text-white transition-colors duration-200 border-t border-gray-800 mt-4"
                            >
                                ← Collapse Details
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Copied Toast */}
                {showCopiedToast && (
                    <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-green-500 text-white text-xs px-3 py-1 rounded-md shadow-lg z-30">
                        ID copied!
                    </div>
                )}
            </div>
        </motion.div>
    );
};
